import React, { useMemo } from "react";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import TablePaginationFooter from "components/common/TablePaginationFooter";
import CardDropdown from "components/common/CardDropdown";
import { Badge, Spinner } from "react-bootstrap";
import { Dropdown } from "react-bootstrap";
import useFormattedDate from "hooks/useFormatDate";

const truncate = (value, limit = 45) => {
  const text = String(value || "");
  if (text.length <= limit) return text;
  return `${text.slice(0, limit)}...`;
};

const getStatusVariant = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "DISABLED" || normalized === "INACTIVE") return "secondary";
  if (normalized === "PENDING") return "warning";
  if (normalized === "FAILED") return "danger";
  return "success";
};

export default function EmailAccountsTable({
  accounts,
  loading,
  page,
  pageSize,
  totalPages,
  totalElements,
  setPage,
  setPageSize,
  onView,
  onDeleteById,
  onDeleteByEmail,
}) {
  const { formatDateTime } = useFormattedDate();

  const columns = useMemo(
    () => [
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) => {
          const item = row.original || {};
          const email = item.email || item.address || item.username || "Unknown";
          return <span className="fw-semibold">{truncate(email, 40)}</span>;
        },
        meta: { headerProps: { className: "text-900" } },
      },
      {
        accessorKey: "displayName",
        header: "Display Name",
        cell: ({ row }) => {
          const item = row.original || {};
          const name = item.displayName || item.name || "-";
          return <span className="text-muted">{truncate(name, 40)}</span>;
        },
        meta: { headerProps: { className: "text-900" } },
      },
      {
        accessorKey: "provider",
        header: "Provider",
        cell: ({ row }) => {
          const item = row.original || {};
          const provider = item.provider || item.type || item.service || "-";
          return <span className="text-muted">{truncate(provider, 30)}</span>;
        },
        meta: { headerProps: { className: "text-900" } },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => {
          const status = row.original?.status || row.original?.state || "ACTIVE";
          return <Badge bg={getStatusVariant(status)}>{status}</Badge>;
        },
        meta: { headerProps: { className: "text-900 text-center" }, cellProps: { className: "text-center" } },
      },
      {
        accessorKey: "createdAt",
        header: "Created At",
        cell: ({ row }) => {
          const createdAt = row.original?.createdAt || row.original?.createdOn;
          return <span className="text-muted">{formatDateTime(createdAt)}</span>;
        },
        meta: { headerProps: { className: "text-900" } },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const item = row.original || {};
          return (
            <CardDropdown drop="start">
              <div className="py-2">
                <Dropdown.Item onClick={() => onView?.(item.id)}>
                  View
                </Dropdown.Item>
                <Dropdown.Item onClick={() => onDeleteById?.(item.id)}>
                  Delete by ID
                </Dropdown.Item>
                {item.email && (
                  <Dropdown.Item onClick={() => onDeleteByEmail?.(item.email)}>
                    Delete by Email
                  </Dropdown.Item>
                )}
              </div>
            </CardDropdown>
          );
        },
        meta: { headerProps: { className: "text-900 text-center" }, cellProps: { className: "text-center" } },
      },
    ],
    [formatDateTime, onDeleteByEmail, onDeleteById, onView],
  );

  const table = useAdvanceTable({
    data: accounts,
    columns,
    sortable: true,
    pagination: true,
    perPage: pageSize,
  });

  if (loading && accounts.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center py-4">
        <Spinner animation="border" variant="success" />
      </div>
    );
  }

  return (
    <>
      <AdvanceTableProvider {...table}>
        <AdvanceTable
          headerClassName="bg-300 text-nowrap align-middle"
          rowClassName="align-middle white-space-nowrap fs-12"
          tableProps={{
            bordered: true,
            striped: true,
            className: "mb-0 overflow-hidden",
          }}
        />
        <div className="mt-3">
          <TablePaginationFooter
            page={page}
            totalPages={totalPages || 0}
            totalElements={totalElements || 0}
            onPrev={
              page > 0 && totalElements > 0
                ? () => setPage((prev) => Math.max(prev - 1, 0))
                : undefined
            }
            onNext={
              page < totalPages - 1 && totalElements > 0
                ? () => setPage((prev) => prev + 1)
                : undefined
            }
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
            pageSizeOptions={[10, 20, 30, 50, 100]}
          />
        </div>
      </AdvanceTableProvider>
    </>
  );
}
